import { Component, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA, resource } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { MembershipService } from '../../../core/services/membership.service';
import { AppDataTable, DataColumn } from '../../../shared/components/app-data-table/app-data-table';
import { AppBadge } from '../../../shared/components/app-badge/app-badge';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppMetrics, MetricItem } from '../../../shared/components/app-metrics/app-metrics';
import { MembershipPlanDialog } from '../membership-plan-dialog/membership-plan-dialog';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-membership-admin-list',
  standalone: true,
  imports: [CommonModule, AppDataTable, AppBadge, AppButton, AppMetrics, MembershipPlanDialog],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="animate-fade-in">
        <app-data-table title="Catálogo de Planes"
            description="Configura los planes de suscripción, precios y características para los negocios"
            icon="solar:Settings-bold-duotone" [loading]="loading()" 
            [dataSource]="plans()" [columns]="columns" 
            (onReload)="plansResource.reload()"
            [columnTemplates]="{
               'code': codeTmpl,
                'monthlyPrice': priceTmpl,
                'annualDiscount': discountTmpl,
                'isActive': statusTmpl,
                'actions': actionsTmpl  
             }">
            
            <!-- Global Actions Slot -->
            <ng-template #actionGroup *ngIf="authService.hasPermission('memberships:create')">
                <app-button variant="glass" size="md" iconLeft="mdi:plus" [square]="true" (btnClick)="openCreate()" title="Nuevo Plan" class="p-2">
                </app-button>
            </ng-template>
            
            <!-- Metrics Slot -->
            <ng-template #metrics>
                <app-metrics [items]="metricsItems()"></app-metrics>
            </ng-template>

            <ng-template #codeTmpl let-row>
                <span class="text-[11px] font-bold text-foreground/80 uppercase tracking-tight">
                    {{ row.code }}
                </span>
            </ng-template>

            <ng-template #priceTmpl let-row>
                <span class="text-[11px] font-bold text-foreground">
                    $ {{ (row.monthlyPrice || 0).toFixed(2) }} <span class="text-[9px] text-foreground/50 ml-0.5">/mes</span>
                </span>
            </ng-template>

            <ng-template #discountTmpl let-row>
                <div class="flex flex-col gap-1">
                    <span class="text-[10px] font-bold text-emerald-500">
                        Anual: {{ row.annualDiscountPercentage }}% <span class="text-[8px] text-foreground/50 ml-0.5">off</span>
                    </span>
                    <span class="text-[10px] font-bold text-blue-500" *ngIf="row.monthlyDiscountPercentage">
                        Mensual: {{ row.monthlyDiscountPercentage }}% <span class="text-[8px] text-foreground/50 ml-0.5">off</span>
                    </span>
                </div>
            </ng-template>

            <ng-template #statusTmpl let-row>
                <app-badge [variant]="row.isActive ? 'success' : 'danger'">
                    {{ row.isActive ? 'ACTIVO' : 'INACTIVO' }}
                </app-badge>
            </ng-template>

            <ng-template #actionsTmpl let-row>
                <div class="flex items-center justify-end gap-1">
                    <app-button *ngIf="authService.hasPermission('memberships:edit')"
                        variant="glass" size="xs" [square]="true" (btnClick)="openEdit(row)" class="p-1"
                        iconLeft="solar:pen-new-square-bold" title="Editar Plan" color="text-amber-500">
                    </app-button>
                    <app-button *ngIf="authService.hasPermission('memberships:delete')"
                        variant="glass" size="xs" [square]="true" (btnClick)="deletePlan(row._id)" class="p-1"
                        iconLeft="solar:trash-bin-minimalistic-bold" title="Eliminar" color="text-rose-500">
                    </app-button>
                </div>
            </ng-template>

        </app-data-table>

        <app-membership-plan-dialog
            [isOpen]="showPlanDialog()"
            [plan]="selectedPlan()"
            (onClose)="showPlanDialog.set(false)"
            (onSave)="plansResource.reload(); showPlanDialog.set(false)">
        </app-membership-plan-dialog>
    </div>
  `
})
export class MembershipAdminList {
  private membershipService = inject(MembershipService);
  public authService = inject(AuthService);

  plansResource = resource({
    loader: () => lastValueFrom(this.membershipService.adminPlansList())
  });

  plans = computed(() => this.plansResource.value() || []);
  loading = this.plansResource.isLoading;

  selectedPlan = signal<any>(null);
  showPlanDialog = signal(false);

  columns: DataColumn[] = [
    { key: 'code', title: 'Código', dataIndex: 'code' },
    { key: 'name', title: 'Nombre del Plan', dataIndex: 'name' },
    { key: 'monthlyPrice', title: 'Precio Mes', dataIndex: 'monthlyPrice' },
    { key: 'annualDiscount', title: 'Desc. Anual', dataIndex: 'annualDiscountPercentage' },
    { key: 'isActive', title: 'Estado', dataIndex: 'isActive' },
    { key: 'actions', title: '', align: 'right', class: 'w-24' },
  ];

  metricsItems = computed<MetricItem[]>(() => {
    const list = this.plans();
    return [
      { label: 'Planes Activos', value: list.filter(p => p.isActive).length, icon: 'solar:check-circle-bold-duotone', variant: 'success' },
      { label: 'Inactivos', value: list.filter(p => !p.isActive).length, icon: 'solar:close-circle-bold-duotone', variant: 'danger' },
      { label: 'Total', value: list.length, icon: 'solar:bill-list-bold-duotone', variant: 'primary' },
    ];
  });

  openCreate() {
    this.selectedPlan.set(null);
    this.showPlanDialog.set(true);
  }

  openEdit(plan: any) {
    this.selectedPlan.set(plan);
    this.showPlanDialog.set(true);
  }

  deletePlan(id: string) {
    if (confirm('¿Estás seguro de eliminar este plan? Esto podría afectar a negocios que intenten renovar.')) {
        this.membershipService.deletePlan(id).subscribe(() => {
            this.plansResource.reload();
        });
    }
  }
}
